import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Stack,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Model } from '../../models';
import { toast } from 'sonner';
import { useInvalidation } from 'api-read-hook';
import useApiCallable from '../../api/use-api-callable';
import { QuestionRenderer } from '../questionnaire';
import { produce } from 'immer';

type QuestionnaireResponse = {
  question: string;
  answer: string | string[] | number | Record<string, string>;
  questionSlug?: string;
  answerSlug?: string;
};

interface EditPregnancyFertilityModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  healthResponses: QuestionnaireResponse[];
  onboardingResponses: QuestionnaireResponse[];
}

export default function EditPregnancyFertilityModal({
  open,
  onClose,
  userId,
  healthResponses,
  onboardingResponses,
}: EditPregnancyFertilityModalProps) {
  const { invalidateExact } = useInvalidation();
  const callApi = useApiCallable();

  const [formData, setFormData] = useState<Record<string, any>>({});
  const [otherTexts, setOtherTexts] = useState<Record<string, string>>({});

  const pregnancyData = useMemo(
    () => Model.Questionnaire.getPregnancySectionQuestions(),
    [],
  );

  const { pregnancySection, allQuestions, onboardingQuestion } = pregnancyData;

  const getResponseByQuestion = useCallback(
    (questionText: string) => {
      const allResponses = [...healthResponses, ...onboardingResponses];
      const response = allResponses.find((r) => r.question === questionText);
      return response?.answer || '';
    },
    [healthResponses, onboardingResponses],
  );

  useEffect(() => {
    if (!open || allQuestions.length === 0) return;

    const initialData: Record<string, any> = {};
    const initialOtherTexts: Record<string, string> = {};

    allQuestions.forEach((question) => {
      const response = getResponseByQuestion(question.question || '');

      if (question.type === 'multi-select') {
        if (Array.isArray(response)) {
          const validOptions =
            question.options?.map((opt: any) => opt.value) || [];
          const processedValues: string[] = [];
          const customTexts: string[] = [];

          response.forEach((val: string) => {
            if (validOptions.includes(val)) {
              processedValues.push(val);
            } else if (val && val.trim()) {
              if (!processedValues.includes('Other')) {
                processedValues.push('Other');
              }
              customTexts.push(val);
            }
          });

          if (customTexts.length > 0) {
            initialOtherTexts[question.id] = customTexts.join(', ');
          }

          initialData[question.id] = processedValues;
        } else if (response && response !== '-') {
          const responseString = String(response);
          const responseParts = responseString.split(', ').filter(Boolean);
          const validOptions =
            question.options?.map((opt: any) => opt.value) || [];
          const processedValues: string[] = [];
          const customTexts: string[] = [];

          responseParts.forEach((val: string) => {
            if (validOptions.includes(val)) {
              processedValues.push(val);
            } else if (val && val.trim()) {
              if (!processedValues.includes('Other')) {
                processedValues.push('Other');
              }
              customTexts.push(val);
            }
          });

          if (customTexts.length > 0) {
            initialOtherTexts[question.id] = customTexts.join(', ');
          }

          initialData[question.id] = processedValues;
        } else {
          initialData[question.id] = [];
        }
      } else if (question.type === 'number') {
        initialData[question.id] = response
          ? parseInt(String(response), 10)
          : 0;
      } else {
        const validOptions =
          question.options?.map((opt: any) => opt.value) || [];
        if (
          response &&
          response !== '-' &&
          !validOptions.includes(String(response))
        ) {
          initialData[question.id] = 'Other';
          initialOtherTexts[question.id] = String(response);
        } else {
          initialData[question.id] = response || '';
        }
      }
    });

    setFormData(initialData);
    setOtherTexts(initialOtherTexts);
  }, [open, allQuestions, getResponseByQuestion]);

  const formatAnswer = useCallback(
    (value: any, questionId: string, isMultiSelect = false) => {
      if (value === 'Other' && otherTexts[questionId]) {
        return otherTexts[questionId];
      }

      if (isMultiSelect && Array.isArray(value)) {
        const finalValues = [...value];
        if (value.includes('Other') && otherTexts[questionId]) {
          const otherIndex = finalValues.indexOf('Other');
          if (otherIndex > -1) {
            finalValues[otherIndex] = otherTexts[questionId];
          }
        }
        return finalValues.join(', ');
      }

      return value.toString();
    },
    [otherTexts],
  );

  const handleMultiSelectChange = useCallback(
    (questionId: string, value: string, checked: boolean) => {
      setFormData((prev) => {
        const currentValues = prev[questionId] || [];
        if (checked) {
          return { ...prev, [questionId]: [...currentValues, value] };
        } else {
          return {
            ...prev,
            [questionId]: currentValues.filter((v: string) => v !== value),
          };
        }
      });
    },
    [],
  );

  const handleSingleSelectChange = useCallback(
    (questionId: string, value: string) => {
      setFormData((prev) => ({ ...prev, [questionId]: value }));
    },
    [],
  );

  const handleNumberChange = useCallback(
    (questionId: string, value: number) => {
      setFormData((prev) => ({ ...prev, [questionId]: value }));
    },
    [],
  );

  const handleOtherTextChange = useCallback(
    (questionId: string, value: string) => {
      setOtherTexts((prev) => ({ ...prev, [questionId]: value }));
    },
    [],
  );

  const shouldShowQuestion = useCallback(
    (question: any): boolean => {
      if (!question.showIf) return true;

      for (const [dependentQuestionId, allowedValues] of Object.entries(
        question.showIf,
      )) {
        const dependentValue = formData[dependentQuestionId];

        if (Array.isArray(allowedValues)) {
          if (Array.isArray(dependentValue)) {
            const hasMatch = dependentValue.some((val) =>
              allowedValues.includes(val),
            );
            if (!hasMatch) return false;
          } else {
            if (!allowedValues.includes(dependentValue)) return false;
          }
        } else if (
          typeof allowedValues === 'object' &&
          allowedValues &&
          'not' in allowedValues
        ) {
          const notAllowedValues = (allowedValues as any).not;
          if (Array.isArray(dependentValue)) {
            const hasNotAllowed = dependentValue.some((val) =>
              notAllowedValues.includes(val),
            );
            if (hasNotAllowed) return false;
          } else {
            if (notAllowedValues.includes(dependentValue)) return false;
          }
        }
      }

      return true;
    },
    [formData],
  );

  const handleSave = async () => {
    if (!pregnancySection) return;

    const completeOnboardingResponses = produce(
      onboardingResponses,
      (draft) => {
        if (
          onboardingQuestion &&
          formData[onboardingQuestion.id] !== undefined
        ) {
          const value = formData[onboardingQuestion.id];
          const existingIndex = draft.findIndex(
            (r) => r.question === onboardingQuestion.question,
          );

          if (value !== null && value !== '') {
            const answerString = formatAnswer(value, onboardingQuestion.id);
            if (answerString) {
              if (existingIndex >= 0) {
                draft[existingIndex].answer = answerString;
              } else {
                draft.push({
                  question: onboardingQuestion.question || '',
                  answer: answerString,
                });
              }
            }
          } else if (existingIndex >= 0) {
            draft.splice(existingIndex, 1);
          }
        }
      },
    );

    const completeHealthResponses = produce(healthResponses, (draft) => {
      pregnancySection.questions
        .filter((q) => q.id !== 'q37')
        .forEach((question) => {
          const value = formData[question.id];
          const existingIndex = draft.findIndex(
            (r) => r.question === question.question,
          );

          if (value !== undefined && value !== null && value !== '') {
            const answerString = formatAnswer(
              value,
              question.id,
              question.type === 'multi-select',
            );
            if (answerString) {
              if (existingIndex >= 0) {
                draft[existingIndex].answer = answerString;
              } else {
                draft.push({
                  question: question.question || '',
                  answer: answerString,
                });
              }
            }
          } else if (existingIndex >= 0) {
            draft.splice(existingIndex, 1);
          }
        });
    });

    await callApi(`/users/${userId}/questionnaires/before_appointment`, {
      method: 'PATCH',
      jsonBody: {
        responses: completeHealthResponses,
      },
    });

    await callApi(`/users/${userId}/questionnaires/onboarding`, {
      method: 'PATCH',
      jsonBody: {
        responses: completeOnboardingResponses,
      },
    });

    toast.success('Pregnancy & Fertility information updated');
    invalidateExact(`/users/${userId}/questionnaires/before_appointment`);
    invalidateExact(`/users/${userId}/questionnaires/onboarding`);
    onClose();
  };

  if (!pregnancySection) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack>
          <Typography variant="h6">
            Health Profile: Pregnancy and Fertility
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Users have the ability to overwrite your changes in the app
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {allQuestions.map((question) => (
            <QuestionRenderer
              key={question.id}
              question={question}
              value={formData[question.id]}
              otherTexts={otherTexts}
              onMultiSelectChange={handleMultiSelectChange}
              onSingleSelectChange={handleSingleSelectChange}
              onNumberChange={handleNumberChange}
              onOtherTextChange={handleOtherTextChange}
              shouldShowQuestion={shouldShowQuestion}
            />
          ))}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleSave} variant="contained">
          Save Changes
        </Button>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
